import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext();

// Clés localStorage séparées pour admin et client
const STORAGE_KEY_ADMIN = 'younited_admin';
const STORAGE_KEY_CLIENT = 'younited_client';

const getStorageKey = (role) => {
  return role === 'admin' ? STORAGE_KEY_ADMIN : STORAGE_KEY_CLIENT;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fonction pour déterminer quelle session charger basée sur le chemin
  const loadSessionForPath = () => {
    const currentPath = window.location.pathname;
    
    if (currentPath.startsWith('/admin')) {
      // On admin pages, load admin session
      const storedAdmin = localStorage.getItem(STORAGE_KEY_ADMIN);
      if (storedAdmin) {
        return JSON.parse(storedAdmin);
      }
    } else if (currentPath.startsWith('/client') || currentPath === '/login') {
      // On client pages or login, load client session first
      const storedClient = localStorage.getItem(STORAGE_KEY_CLIENT);
      if (storedClient) {
        return JSON.parse(storedClient);
      }
    }
    
    // Fallback: try both (client first, then admin)
    const storedClient = localStorage.getItem(STORAGE_KEY_CLIENT);
    if (storedClient) {
      return JSON.parse(storedClient);
    }
    
    const storedAdmin = localStorage.getItem(STORAGE_KEY_ADMIN);
    if (storedAdmin) {
      return JSON.parse(storedAdmin);
    }
    
    return null;
  };

  useEffect(() => {
    const sessionUser = loadSessionForPath();
    if (sessionUser) {
      setUser(sessionUser);
    }
    setLoading(false);
  }, []);

  // Recharger la session quand le chemin change
  useEffect(() => {
    const handlePathChange = () => {
      const sessionUser = loadSessionForPath();
      if (sessionUser) {
        setUser(sessionUser);
      }
    };

    // Écouter les changements d'URL
    window.addEventListener('popstate', handlePathChange);
    return () => window.removeEventListener('popstate', handlePathChange);
  }, []);

  const login = async (email, password) => {
    try {
      const result = await authService.login(email, password);
      
      if (result.success && result.user) {
        const storageKey = getStorageKey(result.user.role);
        setUser(result.user);
        localStorage.setItem(storageKey, JSON.stringify(result.user));
        return { success: true, user: result.user };
      }
      
      return { success: false, error: result.error || 'Identifiants incorrects' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Erreur de connexion' };
    }
  };

  const logout = () => {
    if (user) {
      const storageKey = getStorageKey(user.role);
      localStorage.removeItem(storageKey);
    }
    setUser(null);
  };

  const updatePassword = async (newPassword) => {
    if (user) {
      try {
        await authService.updatePassword(user.id, newPassword);
        return { success: true };
      } catch (error) {
        console.error('Password update error:', error);
        return { success: false };
      }
    }
    return { success: false };
  };

  const updateUser = (updatedUserData) => {
    const newUser = { ...user, ...updatedUserData };
    setUser(newUser);
    const storageKey = getStorageKey(newUser.role);
    localStorage.setItem(storageKey, JSON.stringify(newUser));
  };

  const value = {
    user,
    login,
    logout,
    updatePassword,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isClient: user?.role === 'client',
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
